import React, { useRef, useState, useEffect } from "react";
import Compressor from "compressorjs";
import { MdCameraAlt, MdAccessibilityNew } from 'react-icons/md';
interface CameraCaptureProps {
  onCapture: (file: File) => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture }) => {
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch((err) => {
        console.error("Video play failed:", err);
        setError("Erreur lors du démarrage de la vidéo.");
      });
    }
  }, [stream, capturing]);

  const openCamera = async () => {
    setError(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 } 
        },
        audio: false,
      });
      setStream(mediaStream);
      setCapturing(true);
    } catch (err: any) {
      console.error("Camera access error:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Accès refusé. Veuillez autoriser la caméra dans les réglages de votre navigateur.");
      } else {
        setError("Impossible d'accéder à la caméra. Vérifiez qu'elle n'est pas déjà utilisée.");
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setStream(null);
    setCapturing(false);
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;

      new Compressor(blob, {
        quality: 0.8,
        maxWidth: 2048,
        success(result) {
          const file = new File([result], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
          onCapture(file);
          stopCamera();
        },
      });
    }, "image/jpeg");
  };

  return (
    <div className="flex flex-col items-center justify-center">
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm border border-red-200">
          {error}
        </div>
      )}

      {!capturing ? (
       <button
				onClick={openCamera}
				className="flex items-center gap-3 px-4 py-2 mt-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-[0_5px_15px_rgba(37,99,235,0.3)] transition-all hover:-translate-y-0.5 active:translate-y-0 group"
				title="Prendre une photo"
			>
				<MdCameraAlt className="w-5 h-5 transition-transform group-hover:scale-110" />
				
				<span className="text-sm font-medium pr-1">
					Prendre photo
				</span>
			</button>
      ) : (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          
          <div className="absolute bottom-10 left-0 right-0 flex justify-around items-center px-10">
            <button
              onClick={stopCamera}
							type="button"
              className="p-4 bg-white/20 backdrop-blur-md text-white rounded-full hover:bg-white/30"
            >
              Annuler
            </button>
            
            <button
              onClick={takePhoto}
							type="button"
              className="w-20 h-20 bg-white border-4 border-gray-300 rounded-full flex items-center justify-center active:scale-95 transition shadow-2xl"
            >
              <div className="w-16 h-16 border-2 border-black/10 rounded-full"></div>
            </button>
            
            <div className="w-[72px]"></div>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden"></canvas>
    </div>
  );
};

export default CameraCapture;